import React from "react";
import {
  Box,
  Typography,
  Grid,
  Stack,
} from "@mui/material";
import {
  HelpOutline as HelpIcon,
  TouchAppOutlined as PickStepIcon,
  VerifiedUserOutlined as FairStepIcon,
  MonetizationOnOutlined as WinStepIcon,
  RocketLaunchOutlined as RocketIcon,
} from "@mui/icons-material";
import { colors, borderRadiusPx } from "../../../../../theme";
import { LOTTERY_NAMES } from "../../../../../static/Constants";

import HypersonicImg from "../../../../../static/Images/Hypersonic.png";
import BallasticImg from "../../../../../static/Images/Ballastic.png";
import CruisImg from "../../../../../static/Images/cruis.png";
import InitiationImg from "../../../../../static/Images/Initiation.png";

const TIERS = [
  {
    tier: "Hypersonic",
    level: "Level 4",
    desc: "Apex multi-stage propulsion with accumulated maximum jackpot.",
    img: HypersonicImg,
    badgeColor: colors.amber[600],
    bgColor: colors.amber[50],
    borderColor: colors.amber[200],
  },
  {
    tier: "Ballistic",
    level: "Level 3",
    desc: "High-altitude trajectory for experienced high-stakes participants.",
    img: BallasticImg,
    badgeColor: colors.purple[600],
    bgColor: colors.purple[50],
    borderColor: colors.purple[200],
  },
  {
    tier: "Cruise",
    level: "Level 2",
    desc: "Balanced precision launch dynamics with enhanced payout rates.",
    img: CruisImg,
    badgeColor: colors.blue[600],
    bgColor: colors.blue[50],
    borderColor: colors.blue[200],
  },
  {
    tier: "Initiation",
    level: "Level 1",
    desc: "Standard launch coordinates ideal for all new mission pilots.",
    img: InitiationImg,
    badgeColor: colors.emerald[600],
    bgColor: colors.emerald[50],
    borderColor: colors.emerald[200],
  },
];

const STEPS = [
  {
    step: "01",
    icon: PickStepIcon,
    title: `Choose a ${LOTTERY_NAMES.ROCKETBET} Draw`,
    desc: "Browse live missile rounds with your preferred entry tier, ticket fee, and accumulated jackpot prize pool.",
  },
  {
    step: "02",
    icon: FairStepIcon,
    title: "Lock 1 Lucky Trajectory",
    desc: "Select your 1 lucky target number on the missile keypad grid before the rocket launch countdown reaches zero.",
  },
  {
    step: "03",
    icon: WinStepIcon,
    title: "Instant VRF Payout",
    desc: "Chainlink VRF draws the winning trajectory on-chain. Smart contracts distribute winnings directly to the winner's wallet!",
  },
];

const MissileHowToPlay = () => {
  return (
    <Box sx={{ mt: 6, mb: 4 }}>
      {/* 4 Missile Progressive Tiers Showcase */}
      <Box
        sx={{
          bgcolor: colors.dashboard.cardBg,
          borderRadius: borderRadiusPx.xl,
          border: `1px solid ${colors.dashboard.cardBorder}`,
          p: { xs: 3, md: 4 },
          mb: 4,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: borderRadiusPx.md,
              bgcolor: colors.red[50],
              color: colors.red[600],
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <RocketIcon sx={{ fontSize: 20 }} />
          </Box>
          <Box>
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, color: colors.slate[900], fontSize: "1.1rem" }}
            >
              {`4 ${LOTTERY_NAMES.ROCKETBET} Progressive Tiers`}
            </Typography>
            <Typography variant="body2" sx={{ color: colors.slate[500] }}>
              Explore progressive missile stages from Initiation to Hypersonic jackpots
            </Typography>
          </Box>
        </Stack>

        <Grid container spacing={2.5}>
          {TIERS.map((item, idx) => (
            <Grid item xs={12} sm={6} md={3} key={idx}>
              <Box
                sx={{
                  p: 2.5,
                  bgcolor: colors.slate[50],
                  borderRadius: borderRadiusPx.lg,
                  border: `1px solid ${colors.slate[200]}`,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  textAlign: "center",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    transform: "translateY(-3px)",
                    borderColor: item.badgeColor,
                  },
                }}
              >
                <Box
                  component="img"
                  src={item.img}
                  alt={item.tier}
                  sx={{
                    width: 54,
                    height: 54,
                    objectFit: "contain",
                    mb: 1.5,
                  }}
                />
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 800, color: colors.slate[900], mb: 0.25 }}
                >
                  {item.tier}
                </Typography>
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.5,
                    px: 1.25,
                    py: 0.35,
                    borderRadius: borderRadiusPx.full,
                    bgcolor: item.bgColor,
                    border: `1px solid ${item.borderColor}`,
                    mb: 1.5,
                  }}
                >
                  <RocketIcon sx={{ fontSize: 13, color: item.badgeColor }} />
                  <Typography
                    variant="caption"
                    sx={{ color: item.badgeColor, fontWeight: 700, fontSize: "0.72rem" }}
                  >
                    {item.level}
                  </Typography>
                </Box>
                <Typography
                  variant="body2"
                  sx={{ color: colors.slate[600], fontSize: "0.8rem", lineHeight: 1.5 }}
                >
                  {item.desc}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* 3 Steps Overview */}
      <Box
        sx={{
          bgcolor: colors.dashboard.cardBg,
          borderRadius: borderRadiusPx.xl,
          border: `1px solid ${colors.dashboard.cardBorder}`,
          p: { xs: 3, md: 4 },
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: borderRadiusPx.md,
              bgcolor: colors.red[50],
              color: colors.red[600],
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <HelpIcon sx={{ fontSize: 20 }} />
          </Box>
          <Box>
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, color: colors.slate[900], fontSize: "1.1rem" }}
            >
              {`How Autobet ${LOTTERY_NAMES.ROCKETBET} Works`}
            </Typography>
            <Typography variant="body2" sx={{ color: colors.slate[500] }}>
              A 3-step decentralized rocket number lottery
            </Typography>
          </Box>
        </Stack>

        <Grid container spacing={3}>
          {STEPS.map((item, idx) => {
            const StepIcon = item.icon;
            return (
              <Grid item xs={12} md={4} key={idx}>
                <Box
                  sx={{
                    p: 2.5,
                    bgcolor: colors.slate[50],
                    borderRadius: borderRadiusPx.lg,
                    border: `1px solid ${colors.slate[200]}`,
                    height: "100%",
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: borderRadiusPx.md,
                        bgcolor: colors.red[100],
                        color: colors.red[700],
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <StepIcon sx={{ fontSize: 18 }} />
                    </Box>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 800,
                        color: colors.red[400],
                        fontSize: "0.85rem",
                      }}
                    >
                      {item.step}
                    </Typography>
                  </Stack>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 700, color: colors.slate[900], mb: 0.5 }}
                  >
                    {item.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: colors.slate[600], fontSize: "0.82rem", lineHeight: 1.5 }}
                  >
                    {item.desc}
                  </Typography>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Box>
    </Box>
  );
};

export default MissileHowToPlay;
